import { Button, Input, InputPassword } from '@vrplatform/components';
import { Logo } from '@vrplatform/components-legacy';
import { useAppName } from '@vrplatform/theme';
import {
  Anchor,
  Box,
  Checkbox,
  Modal,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { validateEmail } from '../../../../utils';
import { OnboardingSelectCard } from '../../components/OnboardingSelectCard';
import type { SignUpFormInputs, useSignInForm } from '../../hooks';
import { InputWrapper } from './InputWrapper';
import { useWasLoggedInBefore } from './useWasLoggedInBefore';

type Props = ReturnType<typeof useSignInForm> & {
  setTeamType: (type: SignUpType | null) => void;
};

type SignUpType = SignUpFormInputs['type'];

const ChooseType = ({ setType }: { setType: (type: SignUpType) => void }) => {
  const [opened, handlers] = useDisclosure(false);
  const { appName } = useAppName();
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <Stack
        maw={450}
        gap="lg"
        flex={0.8}
        align="stretch"
        justify="center"
        mb="xl"
      >
        <OnboardingSelectCard
          icon="UserIcon"
          title="Property Manager"
          onClick={() => setType('propertyManager')}
          description="I manage properties, issue statements, and handle fees."
        />
        <OnboardingSelectCard
          icon="Calculator2Icon"
          title="Accounting Partner"
          onClick={() => setType('partner')}
          description="I manage accounts, teams, and financial reporting."
        />
        <OnboardingSelectCard
          icon="HomeIcon"
          title="Listing Owner"
          onClick={handlers.open}
          description="I am an owner and want to view statements. I have an invitation."
        />
      </Stack>
      <Modal
        opened={opened}
        onClose={handlers.close}
        radius="md"
        size={'27.5rem'}
        styles={{
          inner: {
            paddingTop: '20dvh!important',
          },
        }}
      >
        <Stack
          align="center"
          maw="20rem"
          mx="auto"
          mb="3rem"
          mt="1rem"
          gap="2rem"
        >
          <Logo width={40} />

          <Box>
            <Title order={4} ta="center" mb="sm">
              Listing Owner needs to be invited
            </Title>
            <Text ta="center" component="p" m={0} size="sm">
              Please contact your property manager to invite you to your{' '}
              {appName} account.
            </Text>
          </Box>
          <Button onClick={handlers.close} sx={{ width: '100%' }}>
            Okay, understood.
          </Button>
        </Stack>
      </Modal>
    </Box>
  );
};

const trapSpacesForRequiredFields = (value: string) => !!value.trim();

export const SignUp = ({ loading, submitSignUp, setTeamType }: Props) => {
  const wasLoggedInBefore = useWasLoggedInBefore();

  const { control, handleSubmit, formState, watch, setValue } =
    useForm<SignUpFormInputs>({});

  const userType = watch('type');

  const goto = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const showChooseRole =
    !userType ||
    pathname === '/choose-role' ||
    (!userType && !wasLoggedInBefore);

  // reset team type when choosing role
  useEffect(() => {
    if (showChooseRole) setTeamType(null);
  }, [setTeamType, showChooseRole]);

  if (pathname === '/') return <Navigate to="/choose-role" replace />;

  if (showChooseRole)
    return (
      <ChooseType
        setType={(type) => {
          setValue('type', type);
          setTeamType(type);
          goto('/sign-up');
        }}
      />
    );

  return (
    <Box
      sx={{
        maxWidth: 400,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 17,
        flex: 0.8,
        justifyContent: 'center',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(submitSignUp)}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing.lg,
            flexWrap: 'nowrap',
            '& > *': {
              flex: 1,
            },
          })}
        >
          <Controller
            control={control}
            name={'firstName'}
            defaultValue={''}
            rules={{ required: true, validate: trapSpacesForRequiredFields }}
            render={({ field, fieldState: { error } }) => (
              <InputWrapper label={'First name'} error={error?.message}>
                <Input
                  {...field}
                  placeholder={'First name'}
                  autoComplete="given-name"
                  error={!!error?.message}
                />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name={'lastName'}
            defaultValue={''}
            rules={{ required: true, validate: trapSpacesForRequiredFields }}
            render={({ field, fieldState: { error } }) => (
              <InputWrapper label={'Last name'} error={error?.message}>
                <Input
                  {...field}
                  placeholder={'Last name'}
                  autoComplete="family-name"
                  error={!!error?.message}
                />
              </InputWrapper>
            )}
          />
        </Box>
        <Controller
          control={control}
          name={'companyName'}
          defaultValue={''}
          rules={{ required: true, validate: trapSpacesForRequiredFields }}
          render={({ field, fieldState: { error } }) => (
            <InputWrapper label={'Company'} error={error?.message}>
              <Input
                {...field}
                placeholder="John's property management"
                autoComplete="off"
                error={!!error?.message}
              />
            </InputWrapper>
          )}
        />
        <Controller
          control={control}
          name={'email'}
          defaultValue={''}
          rules={{
            validate: (value) => validateEmail(value, { required: true }),
          }}
          render={({ field, fieldState: { error } }) => (
            <InputWrapper label={'Email'} error={error?.message}>
              <Input
                {...field}
                placeholder="Email"
                type="email"
                autoComplete="email"
                error={!!error?.message}
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={control}
          name={'password'}
          defaultValue={''}
          rules={{ required: true, validate: trapSpacesForRequiredFields }}
          render={({ field, fieldState: { error } }) => (
            <InputWrapper label={'Password'} error={error?.message}>
              <InputPassword
                {...field}
                value={field.value ?? ''}
                placeholder="Password"
                type="password"
                autoComplete="new-password"
                error={!!error?.message}
              />
            </InputWrapper>
          )}
        />

        <Controller
          control={control}
          name={'policyAccepted'}
          defaultValue={false}
          rules={{
            validate: (value) => {
              if (!value) return 'You must accept the policy to sign up';
              return true;
            },
          }}
          render={({ field: { value, ...field }, fieldState: { error } }) => (
            <Checkbox
              {...field}
              checked={value}
              size="sm"
              mt="md"
              error={!!error?.message}
              label={
                <>
                  I agree to the{' '}
                  <Anchor
                    fw={500}
                    href={'https://www.vrplatform.app/terms'}
                    target="_blank"
                    rel="noopener,noreferrer"
                  >
                    Terms of Service
                  </Anchor>{' '}
                  and{' '}
                  <Anchor
                    fw={500}
                    href={'https://www.vrplatform.app/terms'}
                    target="_blank"
                    rel="noopener,noreferrer"
                  >
                    Privacy Policy
                  </Anchor>
                  .
                </>
              }
            />
          )}
        />

        <Button
          variant="primary"
          loading={loading}
          disabled={!formState.isValid}
          type="submit"
          sx={(theme) => ({
            marginTop: theme.spacing.lg,
            marginBottom: theme.spacing.xs,
          })}
        >
          Sign Up
        </Button>
      </Box>
    </Box>
  );
};
